/* eslint-disable no-console */

import { Auth } from 'aws-amplify';
import userService from '../../../../general/js/user/user-service';
import {
    getOauthReturnUrl,
    getOauthSignInReturnUrl,
    getOauthSignOutReturnUrl,
    getOauthRedirectUri,
    redirectTo,
    reloadPage,
    setOauthSignInReturnUrl,
    setOauthSignOutReturnUrl,
    setOauthFlowReturnUrl,
    getOauthFlowReturnUrl,
    buildCustomOauthState,
    getOauthPathFromRoute,
    getOauthUserJourneyFromRoute,
    deleteFromSessionStorage,
    getParameterByName,
    OauthQueryConstants,
    removeParameterByName,
} from '../../../../general/js/utils';
import { hasValue, stringToBoolean } from '../../../../general/js/general-helpers';
import { IdentityBrokerConfig } from '../../../../general/js/utils/oauth/constants/identity-broker-config';
import { ENDEAVOR_AUTH } from '../../../../general/js/api/endeavor/constants/api-constants';

export const handleOauthSignedOut = ({ allowReload = true, purgeValue = true, useDefaultRedirectUri = true }) => {
    const redirectUri = getOauthSignOutReturnUrl(purgeValue, useDefaultRedirectUri);

    if (!hasValue(redirectUri)) {
        return;
    }

    if (getOauthReturnUrl({ useEncode: false }).toLowerCase() === redirectUri.toLowerCase()) {
        if (allowReload) {
            reloadPage();
        }
    } else {
        redirectTo(redirectUri);
    }
};

export const handleOauthSignedIn = ({ allowReload = true, purgeValue = true, useDefaultRedirectUri = true }) => {
    const redirectUri = getOauthSignInReturnUrl(purgeValue, useDefaultRedirectUri);

    if (!hasValue(redirectUri)) {
        return;
    }

    if (getOauthReturnUrl({ useEncode: false }).toLowerCase() === redirectUri.toLowerCase()) {
        if (allowReload) {
            reloadPage();
        }
    } else {
        redirectTo(redirectUri);
    }
};

export const handleOauthCompleteFlow = () => {
    const redirectUri = getOauthFlowReturnUrl();

    if (!hasValue(redirectUri)) {
        return;
    }

    redirectTo(redirectUri);
};

export const federatedSignIn = async (customState = undefined) => {
    try {
        await Auth.currentSession()
            .then((authSession) => {
                if (!authSession.isValid()) {
                    AmplifyFederatedSignIn(customState);
                }
            })
            .catch(() => {
                AmplifyFederatedSignIn(customState);
            });
    } catch (error) {
        console.error('Error signing in:', error);
    } finally {
        userService.clearLoggedInData();
        AmplifyFederatedSignIn(customState);
    }
};

const AmplifyFederatedSignIn = (customState = undefined) => {
    setOauthSignInReturnUrl();
    deleteFromSessionStorage(ENDEAVOR_AUTH);
    if (hasValue(customState)) {
        Auth.federatedSignIn({ customState });
    } else {
        Auth.federatedSignIn();
    }
};

export const getCurrentUserAttributes = async () => {
    const currentUser = await Auth.currentSession()
        .then((data) => {
            const { jwtToken, payload } = data.idToken;

            return {
                attributes: payload,
                idToken: jwtToken
            };
        })
        .catch((e) => {
            console.error(e);
            return {};
        });

    return currentUser;
};

export const getCurrentAuthenticatedUser = async () => {
    const currentAuthenticatedUser = await Auth.currentAuthenticatedUser({ bypassCache: true })
        .then((user) => {
            return user;
        })
        .catch(() => {
            return {};
        });

    return currentAuthenticatedUser;
};

export const federatedSignOut = async (global = false) => {
    try {
        setOauthSignOutReturnUrl();
        Auth.signOut({ global });
    } catch (error) {
        console.error('Error signing out:', error);
    }
};

export const signUp = (registerRoute = IdentityBrokerConfig.headerRegisterRoute) => {
    setOauthFlowReturnUrl();
    const customState = buildCustomOauthState(getOauthPathFromRoute(registerRoute), getOauthUserJourneyFromRoute(registerRoute));
    federatedSignIn(customState);
};

export const forgotPassword = () => {
    setOauthFlowReturnUrl();
    const customState = buildCustomOauthState(getOauthPathFromRoute(IdentityBrokerConfig.forgotPasswordRoute));
    federatedSignIn(customState);
};

export const updateAccountData = (updateAccountRoute) => {
    try {
        const path = getOauthPathFromRoute(updateAccountRoute);

        if (!path) {
            console.error('Update Account route is not defined');
            return;
        }

        const targetUrl = getOauthRedirectUri(path, true, getOauthUserJourneyFromRoute(updateAccountRoute));

        redirectTo(targetUrl);
    } catch (error) {
        console.error(error);
    }
};

export const changePassword = () => {
    try {
        const path = getOauthPathFromRoute(IdentityBrokerConfig.changePasswordRoute);

        if (!hasValue(path)) {
            console.error('Change Password route is not defined');
            return;
        }

        const targetUrl = getOauthRedirectUri(path, true);

        redirectTo(targetUrl);
    } catch (error) {
        console.error(error);
    }
};

export const mastercardUpgrade = () => {
    try {
        const path = getOauthPathFromRoute(IdentityBrokerConfig.upgradeToOneClubAdvantageRoute);

        if (!hasValue(path)) {
            console.error('Mastercard Upgrade route is not defined');
            return;
        }

        const targetUrl = getOauthRedirectUri(path);
        deleteFromSessionStorage(ENDEAVOR_AUTH);
        redirectTo(targetUrl);
    } catch (error) {
        console.error(error);
    }
};

export const getConfirmedEmailCustomState = () => {
    const confirmedEmailComplete = getParameterByName(OauthQueryConstants.CONFIRMED_EMAIL);
    if (hasValue(confirmedEmailComplete)) {
        removeParameterByName(OauthQueryConstants.CONFIRMED_EMAIL);
        const confirmedEmail = stringToBoolean(confirmedEmailComplete);
        return JSON.stringify({ confirmedEmail });
    }
    return undefined;
};
